import {
  Outlet,
  type ParsedLocation,
  createRootRouteWithContext,
  redirect,
} from '@tanstack/react-router';

import { CommandPalette } from '@/features/command-palette';
import { GlobalAiChatWidget } from '@/features/global-ai-chat';
import { useAuthStore } from '@/lib/auth';
import { featureFlags } from '@/lib/env';
import { EnvMode, getMode } from '@/lib/env/feature-flag';

import { GlobalConfirmDialog } from '../components/Dialog/GlobalConfirmDialog/GlobalConfirmDialog';
import { NotFound } from '../components/Error/NotFound';
import { FullScreenOverlay } from '../components/Loading';
import { useBfcacheSessionCheck } from '../lib/auth/hooks';
import { authService } from '../lib/auth/ory/service';
import useChangeLanguageByKeyPress from '../lib/i18n/hooks/useChangeLanguageByKeyPress';
import type { queryClient } from '../lib/queryClient';
import { useGlobalLoadingStore } from '../lib/store/loading';

/**
 * 認証チェックを実行する
 *
 * whoami APIを呼び出して認証状態をチェックし、
 * 未認証またはMFA認証が必要な場合はサインインページにリダイレクトする
 */
async function checkAuthentication(
  location: ParsedLocation<Record<string, never>>,
): Promise<void> {
  // /auth配下および /me（認証ポータル）の場合は認証チェックをスキップ
  // /me は独自の beforeLoad で Ory 認証チェックを行い、
  // セッション検証（GET /v2/session）の 403 を回避する
  //
  // /oauth2/* は IdP リダイレクト先で login_challenge を保ったまま遷移する必要があるため
  // 認証チェックをスキップし、各ルート側で個別に判定する
  if (
    location.pathname.startsWith('/auth') ||
    location.pathname.startsWith('/me') ||
    location.pathname.startsWith('/oauth2/')
  ) {
    return;
  }

  const status = await authService.getAuthenticationStatus();

  // 未認証またはMFA認証が必要な場合はサインインページにリダイレクト
  if (status === 'unauthenticated' || status === 'requires_mfa') {
    throw redirect({
      to: '/auth/sign-in',
      search: {
        redirect: location.pathname,
      },
      replace: true,
    });
  }
}

export interface RouterContext {
  queryClient: typeof queryClient;
}

// STG で AI チャット機能を許可する organizationId。
// ローカル dev では全 organization で表示し、STG ではこの組織のみに限定する。
const AI_CHAT_ALLOWED_ORGANIZATION_ID = 'OCIL22wq6lK';

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  beforeLoad: async ({ location }) => {
    await checkAuthentication(location);
  },
  notFoundComponent: NotFound,
});

function Root() {
  const { isLoading, message } = useGlobalLoadingStore();
  const organizationId = useAuthStore(
    (state) => state.session?.organization?.organizationId,
  );
  // ローカル dev server (import.meta.env.DEV) とデプロイ DEV 環境は全 organization で表示。
  // STG は指定 organizationId のみ表示。demo / production 環境では非表示。
  const isAiChatWidgetVisible =
    import.meta.env.DEV ||
    getMode() === EnvMode.development ||
    (getMode() === EnvMode.staging &&
      organizationId === AI_CHAT_ALLOWED_ORGANIZATION_ID);

  // 開発者ショートカットとしてcontrol + command + lで日英をトグルする
  useChangeLanguageByKeyPress();

  // bfcache対策: ブラウザバックでページが復元された場合にセッション状態を再チェック
  useBfcacheSessionCheck();

  return (
    <>
      <GlobalConfirmDialog />

      {/* 開発者用のショートカット。Cmd+Kでコマンドパレットを開く。 */}
      {featureFlags.check('NEW_FEATURE') && <CommandPalette />}
      {isAiChatWidgetVisible && <GlobalAiChatWidget />}

      {/* 組織切り替えなどのグローバルな状態変更時はFullScreenOverlayを使用してユーザー操作をブロック */}
      {isLoading && <FullScreenOverlay message={message} />}
      <Outlet />
    </>
  );
}
